import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { throwFromResponse } from '../../lib/apiErrorMessage';
import { userDetailQueryKey } from '../../hooks/useUserDetailQuery';
import type {
  AdminMemberDetail,
  AdminMemberSummary,
} from '../../hooks/useAdminUsersQuery';

/**
 * R6 O — 사업자(기업) 회원 관리.
 *
 * 목록은 기존 회원 admin 엔드포인트(`GET /api/admin/v1/users`)를 memberType=BUSINESS 로
 * 재사용하고, 승인/반려는 신규 전용 엔드포인트(`/business/approve`·`/business/reject`)를
 * 호출한다. 두 신규 엔드포인트는 generated OpenAPI 타입에 아직 없을 수 있어
 * adminFetch(401 refresh 복구 포함) 로 배선한다(users 훅 패턴).
 *
 * 계약: worklogs/qa-2026-06-17-r6/ID-O-be.md (contract owner: be2).
 */

/** 사업자회원 관리 화면이 노출하는 상태 탭(대기/승인/반려). */
export type BusinessMemberStatus = 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED';

export interface BusinessMembersQueryParams {
  status: BusinessMemberStatus;
  keyword?: string | undefined;
  /**
   * QA-430 — 승인 완료 탭 등급 필터(다중). 1개면 BE 가 이미 받는 gradeId 로, 2개 이상은 gradeIds(반복)로 보낸다
   * (BE gradeIds 반영 전에는 2개 이상 고르면 등급으로 걸러지지 않는다 — 전달 문서).
   */
  gradeIds?: ReadonlyArray<string> | undefined;
  page?: number | undefined;
  size?: number | undefined;
}

export interface BusinessMembersPage {
  items: ReadonlyArray<AdminMemberSummary>;
  meta: { total: number; page: number; size: number };
}

interface AdminMemberListEnvelope {
  success: boolean;
  data?: {
    items?: AdminMemberSummary[];
    content?: AdminMemberSummary[];
    meta?: { total: number; page: number; size: number };
    totalElements?: number;
    number?: number;
    size?: number;
  } | null;
}

const buildQuery = (params: BusinessMembersQueryParams): string => {
  const q = new URLSearchParams();
  q.set('memberType', 'BUSINESS');
  q.set('status', params.status);
  if (params.keyword) q.set('keyword', params.keyword);
  const grades = [...new Set(params.gradeIds ?? [])];
  if (grades.length === 1) q.set('gradeId', grades[0]!);
  grades.forEach((g) => q.append('gradeIds', g));
  if (params.page != null) q.set('page', String(Math.max(0, params.page - 1)));
  if (params.size != null) q.set('size', String(params.size));
  return q.toString();
};

export const businessMembersQueryKey = (params: BusinessMembersQueryParams) =>
  ['admin', 'users', 'business', params] as const;

export const useBusinessMembersQuery = (params: BusinessMembersQueryParams) =>
  useQuery<BusinessMembersPage>({
    queryKey: businessMembersQueryKey(params),
    queryFn: async () => {
      const qs = buildQuery(params);
      const res = await adminFetch(`/api/admin/v1/users?${qs}`);
      if (!res.ok) await throwFromResponse(res, '사업자회원 목록을 불러오지 못했습니다.');
      const env = (await res.json()) as AdminMemberListEnvelope;
      const d = env.data;
      const items = d?.items ?? d?.content ?? [];
      const meta = d?.meta ?? {
        total: d?.totalElements ?? items.length,
        page: d?.number ?? 0,
        size: d?.size ?? 20,
      };
      return { items, meta };
    },
  });

interface ApproveVars {
  userId: string;
  /** 승인 시 함께 부여할 등급(미선택=등급 미부여). */
  gradeId?: string | null;
}

interface RejectVars {
  userId: string;
  reason?: string | null;
}

const invalidateBusiness = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ['admin', 'users', 'business'] });
  // 일반 회원목록/상세도 상태가 바뀌므로 함께 무효화.
  void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
};

export const useBusinessApproveMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminMemberDetail | null, Error, ApproveVars>({
    mutationFn: async ({ userId, gradeId }) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/business/approve`, {
        method: 'POST',
        body: JSON.stringify({ gradeId: gradeId ?? null }),
      });
      if (!res.ok) await throwFromResponse(res, '사업자회원 승인에 실패했습니다.');
      const env = (await res.json().catch(() => null)) as
        | { data?: AdminMemberDetail | null }
        | null;
      return env?.data ?? null;
    },
    onSuccess: () => invalidateBusiness(qc),
  });
};

export const useBusinessRejectMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminMemberDetail | null, Error, RejectVars>({
    mutationFn: async ({ userId, reason }) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/business/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason?.trim() ? reason.trim() : null }),
      });
      if (!res.ok) await throwFromResponse(res, '사업자회원 반려에 실패했습니다.');
      const env = (await res.json().catch(() => null)) as
        | { data?: AdminMemberDetail | null }
        | null;
      return env?.data ?? null;
    },
    onSuccess: () => invalidateBusiness(qc),
  });
};

/**
 * QA-382 — 목록 응답(AdminMemberSummary)에 상호·사업자등록번호가 없어 목록 칸이 늘 '—' 였다(BE 는 상세에만 준다).
 * BE 가 목록에 넣기 전까지, 두 값이 빈 행만 회원 상세(마스킹 그대로, unmask=false)를 조회해 채운다. 한 페이지(최대 20건) 범위.
 * BE 가 목록에 넣으면 빈 행이 없어 추가 조회도 하지 않는다.
 *
 * QA-430 — 등급(gradeId)도 같은 방식으로 채운다. 목록 응답에 gradeId 키 자체가 없을 때(BE 반영 전)만 상세를 본다 —
 * BE 가 목록에 넣으면(값이 null 이어도) 조회하지 않는다.
 */
export const useBusinessInfoFill = (
  items: ReadonlyArray<AdminMemberSummary>,
): ReadonlyArray<AdminMemberSummary> => {
  const missing = items.filter(
    (m) => (!m.businessName && !m.businessRegistrationNo) || !('gradeId' in m),
  );
  const results = useQueries({
    queries: missing.map((m) => ({
      queryKey: userDetailQueryKey(m.userId, false),
      queryFn: async (): Promise<AdminMemberDetail> => {
        const res = await adminFetch(`/api/admin/v1/users/${m.userId}`);
        if (!res.ok) await throwFromResponse(res, '회원 정보를 불러오지 못했습니다.');
        const env = (await res.json()) as { data?: AdminMemberDetail | null };
        if (!env.data) throw new Error('회원 정보 응답이 비어 있습니다.');
        return env.data;
      },
      staleTime: 60_000,
    })),
  });
  const filled = new Map<string, AdminMemberDetail>();
  missing.forEach((m, i) => {
    const d = results[i]?.data;
    if (d) filled.set(m.userId, d);
  });
  if (filled.size === 0) return items;
  return items.map((m) => {
    const d = filled.get(m.userId);
    return d
      ? {
          ...m,
          businessName: m.businessName || (d.businessName ?? null),
          businessRegistrationNo: m.businessRegistrationNo || (d.businessRegistrationNo ?? null),
          gradeId: 'gradeId' in m ? m.gradeId : ((d as { gradeId?: string | null }).gradeId ?? null),
        }
      : m;
  });
};
